import aiAssistantAudioLevelConfig from '../../consts/aiAssistant/aiAssistantAudioLevelConfig';
import animationDurations from '../../consts/animation/animationDurations';
import {getSmoothedLevel} from '../../utils/motionUtils';
import log from '../logger/log';
import aiAssistantVoiceLevelService from './aiAssistantVoiceLevelService';

/**
 * Meters a stream's loudness and publishes it as the smoothed voice level. The analyser isn't connected to
 * the output, so the microphone is never played back. `startMicrophone()` opens and owns its stream (QA sandbox).
 */

const {FFT_SIZE, SILENCE_DBFS, LOUD_DBFS, SILENT_RMS, ATTACK_S, RELEASE_S} = aiAssistantAudioLevelConfig;
const {MAX_FRAME_MS} = animationDurations;

let audioContext = null;
let analyser = null;
let samples = null;
let mediaStream = null;
let isStreamOwned = false;
let frameId = 0;
let level = 0;
let previousTime = 0;

// dBFS, not raw RMS: speech peaks far below full scale and loudness is perceived logarithmically
const getLevelFromRms = (rms) => {
    if (rms < SILENT_RMS) return 0;

    const dbfs = 20 * Math.log10(rms);

    return aiAssistantVoiceLevelService.clampLevel((dbfs - SILENCE_DBFS) / (LOUD_DBFS - SILENCE_DBFS));
};

const readLevel = (time) => {
    frameId = requestAnimationFrame(readLevel);

    analyser.getFloatTimeDomainData(samples);

    const sumOfSquares = samples.reduce((sum, sample) => sum + sample * sample, 0);
    const frameS = Math.min((time - previousTime) / 1000, MAX_FRAME_MS / 1000);

    previousTime = time;
    level = getSmoothedLevel(
        level,
        getLevelFromRms(Math.sqrt(sumOfSquares / samples.length)),
        frameS,
        ATTACK_S,
        RELEASE_S
    );

    aiAssistantVoiceLevelService.setLevel(level);
};

const stop = () => {
    const wasMetering = !!frameId;

    cancelAnimationFrame(frameId);
    frameId = 0;
    level = 0;
    previousTime = 0;

    // Only stop a stream we opened - the session's capture outlives the meter
    if (isStreamOwned) mediaStream?.getTracks().forEach((track) => track.stop());

    try {
        audioContext?.close();
    } catch (e) {
        log.warn('aiAssistantAudioLevelService: closing the audio context failed', e);
    }

    audioContext = null;
    analyser = null;
    samples = null;
    mediaStream = null;
    isStreamOwned = false;

    // Reset only our own level - another source may be publishing
    if (wasMetering) aiAssistantVoiceLevelService.reset();
};

const start = async (stream, {isOwned = false} = {}) => {
    // One meter at a time
    stop();

    try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;

        if (!AudioContextClass || !stream) return false;

        mediaStream = stream;
        isStreamOwned = isOwned;
        audioContext = new AudioContextClass();

        // iOS starts the context suspended, and then it delivers no samples
        await audioContext.resume();

        analyser = audioContext.createAnalyser();
        analyser.fftSize = FFT_SIZE;
        samples = new Float32Array(analyser.fftSize);
        audioContext.createMediaStreamSource(stream).connect(analyser);

        frameId = requestAnimationFrame(readLevel);

        return true;
    } catch (e) {
        log.warn('aiAssistantAudioLevelService: audio metering failed to start', e);
        stop();

        return false;
    }
};

const startMicrophone = async () => {
    try {
        const stream = await navigator.mediaDevices?.getUserMedia({audio: true});

        if (!stream) return false;

        return start(stream, {isOwned: true});
    } catch (e) {
        log.warn('aiAssistantAudioLevelService: microphone metering failed to start', e);

        return false;
    }
};

export default {start, startMicrophone, stop, getLevelFromRms};
